import {
  createElement,
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  ReactNode,
  useState,
} from 'react';
import { ContentDataProvider } from '@sas/nova-core/services/drive';

/**
 * High level configuration values for the content components.
 */
export interface ContentConfigContextValue {
  // A baseUrl to enable running in crossorigin situations, primiarly used by the Content SDk
  baseUrl?: string;
  // A data provider which will be used by all descendant content components unless overridden
  dataProvider?: ContentDataProvider;
}

export const ContentConfigContext = createContext<ContentConfigContextValue>({});
export const ContentConfigDispatch = createContext<
  Dispatch<SetStateAction<ContentConfigContextValue>> | undefined
>(undefined);

/**
 * Retrieve the current configuration
 */
export const useContentConfig = () => useContext(ContentConfigContext);

/**
 * Update the current configuration
 */
export const useContentConfigDispatch = () => useContext(ContentConfigDispatch);

export interface ContentConfigProviderProps {
  initialValue: ContentConfigContextValue;
  children?: ReactNode;
}

/**
 * Provides high level configuration props for content components. In general these are values that
 * will rarely change during runtime.
 * @param initialValue
 * @param children
 */
export const ContentConfigProvider = ({ initialValue, children }: ContentConfigProviderProps) => {
  const [state, setState] = useState<ContentConfigContextValue>(initialValue);
  return (
    <ContentConfigContext.Provider value={state}>
      <ContentConfigDispatch.Provider value={setState}>{children}</ContentConfigDispatch.Provider>
    </ContentConfigContext.Provider>
  );
};
